<script setup lang="ts">
  import { computed, provide } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, FieldSearchBar, UiModalHeader } from '@/shared/ui';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import type { RecordRevision } from '@/entities/Record';
  import { AddRecordFilterInjectionKey } from '../model/addRecordFilterContext';
  import { useAddRecordModal, type RecordSelection } from '../model/useAddRecordModal';
  import AddRecordTable from './AddRecordTable.vue';

  const props = withDefaults(
    defineProps<{
      isSubmitting?: boolean;
      searchUrl?: string | null;
      maxSelections?: number;
      selectionLimitMessage?: string;
    }>(),
    {
      isSubmitting: false,
      searchUrl: null,
      maxSelections: undefined,
      selectionLimitMessage: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'submit', selections: RecordSelection[]): void;
  }>();

  const searchUrl = computed(() => props.searchUrl);

  const {
    searchFields,
    searchFieldId,
    searchTerm,
    orderOptions,
    sortField,
    sortOrder,
    handleSearch,
    handleSort,

    filterContext,

    quantityOptions,
    selectedQuantity,
    currentPage,
    totalRecordsLabel,
    metadata,
    isShowAllMode,
    hasMoreItems,
    loading,
    loadingMore,
    recordRows,
    handleQuantityChange,
    handlePageChange,

    hasSelectedRevisions,
    selectedRevisionIdList,
    selectedRevisionList,
    allVisibleSelected,
    isSelectionIndeterminate,
    isRevisionSelected,
    toggleRevisionSelection,
    handleToggleAllVisible,
    selectionLimitError,
    canSelectRevision,

    isRecordExpanded,
    toggleRecordExpansion,
    getSortedOlderRevisions,
    viewRecordRevision,
  } = useAddRecordModal({
    searchUrl,
    maxSelections: props.maxSelections,
    selectionLimitMessage: props.selectionLimitMessage,
  });

  const isSubmitDisabled = computed(() => !hasSelectedRevisions.value || props.isSubmitting);

  provide(SortOrderInjectionKey, { sort: sortField, order: sortOrder });
  provide(AddRecordFilterInjectionKey, filterContext);

  function handleClose(): void {
    emit('close');
  }

  function handleSubmit(): void {
    if (isSubmitDisabled.value) {
      return;
    }
    emit('submit', selectedRevisionList.value);
  }

  function handleView(revision: RecordRevision): void {
    viewRecordRevision(revision);
  }
</script>

<template>
  <VueFinalModal
    class="flex items-center justify-center"
    content-class="w-[95vw] max-w-[1100px] overflow-hidden rounded-2xl bg-white-01 shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    click-to-close
    @update:model-value="
      (value: boolean) => {
        if (!value) emit('close');
      }
    "
  >
    <div
      data-testid="add-record-modal"
      class="flex max-h-[90vh] flex-col"
    >
      <UiModalHeader
        class="border-b border-gray-02 px-6 py-4 text-sm"
        title="Adicionar Registro"
        @close="handleClose"
      />

      <div class="flex-1 overflow-y-auto px-6 py-6">
        <FieldSearchBar
          v-model:search-field-id="searchFieldId"
          :search-fields="searchFields"
          :initial-search-term="searchTerm"
          :independent-search-field="true"
          @search="handleSearch"
        />

        <AddRecordTable
          :records="recordRows"
          :order-options="orderOptions"
          :quantity-options="quantityOptions"
          :selected-quantity="selectedQuantity"
          :total-records-label="totalRecordsLabel"
          :metadata="metadata"
          :current-page="currentPage"
          :is-infinite-scroll-mode="isShowAllMode"
          :has-more-items="hasMoreItems"
          :loading="loading"
          :loading-more="loadingMore"
          :all-selected="allVisibleSelected"
          :partially-selected="isSelectionIndeterminate"
          :is-revision-selected="isRevisionSelected"
          :can-select-revision="canSelectRevision"
          :is-record-expanded="isRecordExpanded"
          :get-sorted-older-revisions="getSortedOlderRevisions"
          @update:sort="handleSort"
          @update:quantity="handleQuantityChange"
          @toggle-select="toggleRevisionSelection"
          @toggle-select-all="handleToggleAllVisible"
          @toggle-expand="toggleRecordExpansion"
          @view="handleView"
          @page="handlePageChange"
        />
      </div>

      <div class="flex items-center justify-between gap-6 border-t border-gray-02 px-6 py-4">
        <div class="flex flex-col gap-1">
          <span
            class="text-sm text-gray-03"
            data-testid="add-record-selected-count"
          >
            {{ selectedRevisionIdList.length }} selecionado(s)
          </span>
          <p
            v-if="selectionLimitError"
            class="text-xs text-red-16"
            data-testid="add-record-selection-limit"
          >
            {{ selectionLimitError }}
          </p>
        </div>
        <div class="ml-auto flex items-center gap-6">
        <ColoredButton
          text="Cancelar"
          outlined
          color="gray-03"
          border-color="gray-03"
          data-testid="add-record-cancel"
          @click="handleClose"
        />
        <ColoredButton
          data-testid="add-record-submit"
          text="Adicionar"
          :disabled="isSubmitDisabled"
          @click="handleSubmit"
        />
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>
